import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Search, Package, X,
  RefreshCw, AlertCircle, ChevronRight,
} from "lucide-react";
import socket from "../../../utils/socket";
import "./installedapps.css";

const InstalledApps = () => {
  const { id: agentId } = useParams();
  const navigate = useNavigate();
  const [apps, setApps]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState("");
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    if (!agentId) return;

    const fetchApps = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const res = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/api/installed-apps/${agentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Synchronization failed.");
        const json = await res.json();
        if (json.success && json.data) setApps(json.data.data?.apps || []);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Unable to retrieve application inventory.");
      } finally {
        setLoading(false);
      }
    };

    fetchApps();
    socket.emit("request_agent_update", { agentId, type: "installed_apps" });

    socket.on("installed_apps_update", (update) => {
      if (update.agentId === agentId) {
        setError(null);
        setApps(update.data?.apps || []);
        setLoading(false);
      }
    });

    socket.on("agent_update", (data) => {
      if (data.agentId === agentId && data.status === "online") {
        socket.emit("request_agent_update", { agentId, type: "installed_apps" });
      }
    });

    return () => {
      socket.off("installed_apps_update");
      socket.off("agent_update");
    };
  }, [agentId]);

  const filteredApps = apps.filter((a) =>
    a.name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatInstallDate = (dateStr) => {
    if (!dateStr) return "N/A";
    if (dateStr.length === 8) {
      const y = dateStr.slice(0, 4), m = dateStr.slice(4, 6), d = dateStr.slice(6, 8);
      return new Date(`${y}-${m}-${d}`).toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  /** First letter(s) of app name for the avatar */
  const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  };

  /* ── State screens ── */
  if (loading) return (
    <div className="installed-apps-wrapper app-state-container">
      <RefreshCw size={40} className="animate-spin state-icon loading" />
      <p className="state-text">Inventorying remote software environment...</p>
    </div>
  );

  if (error) return (
    <div className="installed-apps-wrapper app-state-container">
      <AlertCircle size={48} className="state-icon error" />
      <p className="state-text error">{error}</p>
      <button className="gc-icon-btn" onClick={() => navigate(-1)} data-tip="Back" aria-label="Back">
        <ArrowLeft size={15} />
      </button>
    </div>
  );

  /* ── Main view ── */
  return (
    <div className="installed-apps-wrapper">

      {/* ── Header ── */}
      <div className="usb-header">
        <div className="header-left">
          <button className="gc-icon-btn" onClick={() => navigate(-1)} data-tip="Back" aria-label="Back">
            <ArrowLeft size={15} />
          </button>
          <div className="header-text">
            <h1>Software Inventory</h1>
            <p>Active repository for <span className="agent-id">{agentId}</span></p>
            {apps.length > 0 && (
              <span className="header-count">{apps.length} packages</span>
            )}
          </div>
        </div>

        <div className="search-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Filter applications..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="app-search"
          />
        </div>
      </div>

      {/* ── Content ── */}
      {!apps.length ? (
        <div className="app-empty-state">
          <Package size={48} className="empty-icon" />
          <p>No valid software records detected for this node.</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="table-container">
            <div className="table-wrapper">
              <table className="usb-table">
                <thead>
                  <tr>
                    <th>Software Asset</th>
                    <th>Developer / Publisher</th>
                    <th>Discovered On</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app, i) => (
                    <tr key={i} onClick={() => setSelectedApp(app)}>
                      <td className="app-name-cell">{app.name || "System Binary"}</td>
                      <td className="pub-cell">{app.publisher || "—"}</td>
                      <td className="date-cell">{formatInstallDate(app.install_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile card list */}
          <div className="apps-card-list">
            {filteredApps.map((app, i) => (
              <div key={i} className="app-card" onClick={() => setSelectedApp(app)}>
                {/* Avatar */}
                <div className="app-card-icon">{getInitials(app.name || "?")}</div>

                {/* Body */}
                <div className="app-card-body">
                  <div className="app-card-name">{app.name || "System Binary"}</div>
                  <div className="app-card-row">
                    <span className="app-card-publisher">{app.publisher || "—"}</span>
                    <span className="app-card-date">{formatInstallDate(app.install_date)}</span>
                  </div>
                </div>

                {/* Chevron */}
                <ChevronRight size={16} className="app-card-arrow" />
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Detail Modal ── */}
      {selectedApp && (
        <div className="app-modal" onClick={() => setSelectedApp(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>

            <div className="modal-header">
              <h2>Asset Intelligence</h2>
              <button className="modal-close-btn" onClick={() => setSelectedApp(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              {/* Hero */}
              <div className="modal-hero">
                <h3>{selectedApp.name}</h3>
                <span className="modal-hero-ver">
                  <span className="version-badge">v{selectedApp.version || "N/A"}</span>
                </span>
              </div>

              <div className="info-pair">
                <span className="info-label">Publisher</span>
                <span className="info-value">{selectedApp.publisher || "—"}</span>
              </div>

              <div className="info-pair">
                <span className="info-label">Discovery Date</span>
                <span className="info-value">{formatInstallDate(selectedApp.install_date)}</span>
              </div>

              <div className="info-pair">
                <span className="info-label">System Registry Key</span>
                <span className="long-text">{selectedApp.registry_key || "N/A"}</span>
              </div>

              <div className="info-pair">
                <span className="info-label">Deployment Path</span>
                <span className="long-text">{selectedApp.install_location || "Unknown"}</span>
              </div>

              <div className="info-pair">
                <span className="info-label">Decommission Link</span>
                <span className="long-text">{selectedApp.uninstall_string || "N/A"}</span>
              </div>

              <button className="close-btn" onClick={() => setSelectedApp(null)}>
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstalledApps;
